import {
  attachEmployeeResponseSchema,
  employeeSchema,
  type AssignableEmployeeRole,
  type AttachEmployeeResult,
  type Employee,
  type ListResult,
  type PaginationParams,
} from '../model';
import {
  deleteOne,
  getList,
  getOne,
  patchOne,
  postOne,
  type RequestOptions,
} from './http';
import { env } from '@noor/config';

// Merchant Employee Service (`openapi-merchant-profile.json`, "Noor — Merchant
// Profile (merchant)", v1.00.294).
//
// The MERCHANT contour of the service, not the admin one. The panel used to be
// written against the admin spec (v1.00.225), which is a different surface —
// see "What the merchant contour does NOT have" below, because the differences
// all failed silently rather than at compile time.
//
// The roster is deliberately THIN, and the shape of it drives the whole users
// screen — what follows is what the service does and does not offer, since
// several of the gaps look like omissions here rather than in the backend:
//
// | Need              | Endpoint                                     |
// | ----------------- | -------------------------------------------- |
// | list staff        | `GET  merchant-employees` — limit/offset ONLY |
// | one member        | `GET  merchant-employees/{id}`               |
// | attach (≈ create) | `POST merchant-employees` — by PHONE only    |
// | rename            | `PATCH merchant-employees/{id}` — names ONLY |
// | detach (= delete) | `DELETE merchant-employees/{id}`             |
//
// **What the merchant contour does NOT have**, all of which the admin spec did:
//
// - **No branch sub-resource.** `GET/POST .../branches` and
//   `DELETE .../branches/{restaurant_id}` are absent — branches belong to the
//   vertical (eats/pharma), and merchant-profile no longer knows them at all
//   (the spec says so outright on `GetMe`). So there is no per-branch access
//   surface here, and the only removal is the whole detach.
// - **No `restaurant_id`/`merchant_id` on the list.** The token names the
//   brand, so `GET merchant-employees` takes pagination and nothing else.
//   The gateway rejects unknown query params rather than ignoring them, so
//   sending either would fail the whole request.
// - **No attach by `employee_id`.** `v1AttachEmployeeRequest` carries
//   `new_identity` and NOTHING else, so an identity is always named by phone.
//
// There is no `email` anywhere on `v1Employee`, and no permissions service —
// the Figma designs show both, and neither is buildable.

/**
 * Filters for `GET /merchant-employees`.
 *
 * Pagination only. The roster is scoped by the caller's token; there is no
 * branch filter and no brand parameter — see the note above.
 */
export type ListEmployeesParams = PaginationParams;

/**
 * `POST /merchant-employees` body — attach an identity to the merchant.
 *
 * ONE shape, mirroring the merchant contour's `v1AttachEmployeeRequest`:
 * `{ new_identity: { phone, role, … } }` — create-if-absent by PHONE. The
 * phone is the natural key in the merchant contour, so merchant-profile
 * resolves or creates the identity in auth-service itself, and the caller
 * never carries an id between the two services.
 *
 * The admin spec's `{ employee_id }` variant is NOT part of this contour.
 *
 * `role` applies only when no identity with that phone exists yet. For one
 * that does, the ACTUAL role is read from auth and a mismatch with the
 * requested one is an explicit refusal rather than a silent substitution — an
 * admin adding a cashier must not quietly end up with a manager.
 *
 * `merchant_id` is never sent — it comes from the JWT claim (the app's
 * CLAUDE.md §8 "Merchant & branch scope"), and the merchant contour's request
 * has no such field at all.
 */
export interface AttachEmployeeInput {
  phone: string;
  role: AssignableEmployeeRole;
  /**
   * Both names or neither — the spec is explicit that a first name without a
   * last is a typo rather than an intent. Omitted entirely when blank, so a
   * retry never blanks a name an admin already set (the attach is idempotent
   * and does NOT overwrite stored names).
   */
  firstName?: string;
  lastName?: string;
}

/** Build the wire body for {@link AttachEmployeeInput}. */
function toAttachBody(input: AttachEmployeeInput): Record<string, unknown> {
  const firstName = input.firstName?.trim() ?? '';
  const lastName = input.lastName?.trim() ?? '';
  // Send the pair only when BOTH are filled — see the doc comment above.
  const names =
    firstName && lastName ? { first_name: firstName, last_name: lastName } : {};

  return { new_identity: { phone: input.phone, role: input.role, ...names } };
}

export const employeeApi = {
  /** The merchant's staff roster, scoped to the caller's token. */
  list(
    params?: ListEmployeesParams,
    options?: RequestOptions,
  ): Promise<ListResult<Employee>> {
    return getList(
      'merchant-employees',
      employeeSchema,
      params,
      employeeOptions(options),
    );
  },

  /** One staff member by id. */
  get(employeeId: string, options?: RequestOptions): Promise<Employee> {
    return getOne(
      `merchant-employees/${employeeId}`,
      employeeSchema,
      undefined,
      employeeOptions(options),
    );
  },

  /**
   * Attach an identity to the merchant — the closest thing to a "create".
   *
   * Idempotent: attaching an identity already bound to this brand returns the
   * EXISTING row rather than erroring, and does not overwrite its stored names.
   */
  attach(input: AttachEmployeeInput): Promise<AttachEmployeeResult> {
    return postOne(
      'merchant-employees',
      toAttachBody(input),
      attachEmployeeResponseSchema,
      employeeOptions(),
    );
  },

  /**
   * Rename a staff member. `MerchantEmployeeServiceUpdateEmployeeBody` carries
   * `first_name`/`last_name` and NOTHING else — phone, role and password belong
   * to auth-service and cannot be changed from here.
   */
  update(
    employeeId: string,
    input: { firstName: string; lastName: string },
  ): Promise<Employee> {
    return patchOne(
      `merchant-employees/${employeeId}`,
      { first_name: input.firstName, last_name: input.lastName },
      employeeSchema,
      employeeOptions(),
    );
  },

  /**
   * Detach an employee from the merchant — the mirror of {@link attach}, and
   * what the users screen's «Удалить сотрудника» calls.
   *
   * `DELETE merchant-employees/{employee_id}`. The service carries no verb
   * suffixes in its paths: attach creates the binding by POSTing the
   * collection, this removes it by DELETEing the item.
   *
   * **No `merchant_id` is sent, deliberately.** The employee row already knows
   * its brand; the usecase reads it from there and checks the actor's rights
   * against THAT brand. Accepting a brand from the body would let a caller
   * pick whose access to revoke.
   *
   * The row is deleted physically, so this is the only removal the merchant
   * contour offers — the admin spec's per-branch revoke does not exist here.
   */
  detach(employeeId: string): Promise<void> {
    return deleteOne(`merchant-employees/${employeeId}`, undefined, employeeOptions());
  },
};

function employeeOptions(options?: RequestOptions): RequestOptions {
  return { ...options, baseURL: env.VITE_MERCHANT_EMPLOYEE_API_BASE_URL };
}
